// DashboardPage — page-shell composer for landing / overview surfaces (CHG-1238).
//
// Slots: PageHeader → optional SetupProgress → stats strip → 2-column grid of
// (feed | callout). At narrow widths the grid collapses to a single column.
//
// Owns its own <main role="main"> per CLAUDE.md RULE 14 — the CHG-1231
// AppShell intentionally does NOT wrap in <main> so each page is the
// canonical landmark.
//
// Reusable by future δ-CHG flagship pages (CHG-1146 onboarding, CHG-1147
// intelligence canvas).

import type { ReactNode } from "react";
import { PageHeader, type PageHeaderAction } from "./PageHeader";

export interface DashboardPageProps {
  title: string;
  subtitle?: string;
  headerActions?: PageHeaderAction[];
  /** Renders above the stat strip when present (null hides it). */
  setupProgress?: ReactNode;
  /** KPI strip (typically a <StatStrip>). */
  stats: ReactNode;
  /** Main content column (typically a <DonationsFeed>). */
  feed: ReactNode;
  /** Right-rail callout (typically an <AICallout>). */
  callout?: ReactNode;
  /** Shown over the page when initial load is pending. */
  loading?: boolean;
  /** Error state; renders an inline error card with retry CTA when set. */
  error?: Error | null;
  onRetry?: () => void;
}

export function DashboardPage({
  title,
  subtitle,
  headerActions,
  setupProgress,
  stats,
  feed,
  callout,
  loading,
  error,
  onRetry,
}: DashboardPageProps) {
  return (
    <main
      role="main"
      style={{
        maxWidth: 1280,
        margin: "0 auto",
        padding: "16px 24px 48px",
      }}
    >
      <PageHeader title={title} subtitle={subtitle} actions={headerActions} />

      {error && !loading ? (
        <div
          role="alert"
          className="card"
          style={{
            padding: 16,
            marginBottom: 20,
            background: "var(--color-error-tint)",
            borderColor: "var(--color-error)",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ flex: 1, color: "var(--color-text)" }}>
            <div style={{ fontWeight: 500, fontSize: 13 }}>Couldn&rsquo;t load your overview</div>
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 2 }}>
              {error.message || "Unknown error"}
            </div>
          </div>
          {onRetry && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onRetry}
            >
              Retry
            </button>
          )}
        </div>
      ) : null}

      {setupProgress}

      {stats}

      {/*
        Responsive grid: 2-column above 960px, single-column below. CSS grid
        `gridTemplateColumns` cannot pick by viewport on its own, so we use a
        wrapper className + scoped <style> block (CSS-in-JS via inline tag
        keeps the contract local — no global stylesheet edit needed). Tests
        assert the wide layout (jsdom defaults to 1024px); the narrow
        breakpoint is covered by the inline @media rule.
      */}
      <style>{`
        .dashboard-page-grid {
          display: grid;
          gap: 16px;
          align-items: start;
          grid-template-columns: ${callout ? "minmax(0, 2fr) minmax(0, 1fr)" : "minmax(0, 1fr)"};
        }
        @media (max-width: 960px) {
          .dashboard-page-grid {
            grid-template-columns: minmax(0, 1fr);
          }
        }
      `}</style>
      <div
        className="dashboard-page-grid"
        data-testid="dashboard-page-grid"
      >
        <div style={{ minWidth: 0 }}>{feed}</div>
        {callout ? <div style={{ minWidth: 0 }}>{callout}</div> : null}
      </div>
    </main>
  );
}
